import { createContext, useMemo, type ReactNode } from 'react';

import { clearCacheStorage } from '@/hooks/useTranslation';
import type { I18nInstanceType } from '@/types/i18n';
import type {
  CompressorTypes,
  ConvertersTypes,
  DataActionOptions,
  GoogleDataLayer,
  MscEnvironments,
  OrganizeTypes,
} from '@/types/index';
import defaultTranslations from '@/utils/defaultTranslations.json';
import { initInstance } from '@/utils/i18n';
import { getMscServiceUrl } from '@/utils/miscellaneous';

export interface AppContextData {
  mscServiceUrl: string;
  i18n: I18nInstanceType | null;
  dataAction: DataActionOptions;
  onFreeDownloadClick?: () => void;
  downloadWinHref?: string;
  googleDataLayer?: GoogleDataLayer;
  webAppUrl?: string;
}

export const AppContext = createContext<AppContextData>({
  mscServiceUrl: process.env.REACT_APP_MSC_ENVIRONMENT as string,
  i18n: null,
  dataAction: 'universal-converter',
});

interface AppProviderProps {
  children: ReactNode;
  type: ConvertersTypes | CompressorTypes | OrganizeTypes;
  mscEnvironment: MscEnvironments;
  translations: Record<string, string>;
  downloadWinHref?: string;
  onFreeDownloadClick?: () => void;
  googleDataLayer?: GoogleDataLayer;
  webAppUrl?: string;
}

const AppProvider = ({
  children,
  downloadWinHref,
  type,
  mscEnvironment,
  translations,
  onFreeDownloadClick,
  googleDataLayer,
  webAppUrl,
}: AppProviderProps) => {
  const value = useMemo(() => {
    const mscServiceUrl = getMscServiceUrl(mscEnvironment);
    clearCacheStorage();
    const i18n = initInstance(translations, defaultTranslations);
    const dataAction = type;

    return {
      mscServiceUrl,
      i18n,
      dataAction,
      downloadWinHref,
      onFreeDownloadClick,
      googleDataLayer,
      webAppUrl,
    };
  }, [downloadWinHref, type, mscEnvironment, translations, onFreeDownloadClick, googleDataLayer, webAppUrl]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppProvider;
